/**
 * Generate portfolio artifact in STAR format
 * @param {Object} scenario - Scenario data
 * @param {Object} score - Final score breakdown
 * @param {Array} decisions - User decisions made during scenario
 * @param {Array} userActions - User actions tracked during scenario
 * @returns {Object} Portfolio artifact in STAR format
 */
export const generatePortfolioArtifact = (scenario, score, decisions = [], userActions = []) => {
  const company = scenario.company || {};
  
  // Extract key decisions from userActions
  const keyDecisions = extractKeyDecisions(userActions, decisions);
  
  // Extract tradeoffs
  const tradeoffs = extractTradeoffs(decisions, score);
  
  // Extract stakeholder alignment summary
  const stakeholderAlignment = generateStakeholderAlignment(score, userActions);
  
  // Extract final metrics
  const finalMetrics = generateFinalMetrics(score);
  
  // Generate STAR format
  const starFormat = {
    situation: `As a Product Manager at ${company.name || 'the company'}, I was tasked with ${scenario.currentSituation || scenario.description}`,
    task: `My objective was to ${scenario.tasks?.map(t => t.title.toLowerCase()).join(', ') || 'complete the product management scenario'}`,
    action: generateActionStatement(keyDecisions, userActions),
    result: generateResultStatement(finalMetrics, score, stakeholderAlignment)
  };
  
  return {
    problemStatement: scenario.currentSituation || scenario.description,
    company: {
      name: company.name,
      type: company.type,
      stage: company.stage,
      product: company.product
    },
    keyDecisions,
    tradeoffs,
    stakeholderAlignment,
    finalMetrics,
    starFormat,
    // Full text version for easy copy-paste
    fullText: formatFullText(starFormat, keyDecisions, tradeoffs, finalMetrics)
  };
};

function extractKeyDecisions(userActions, decisions) {
  // Extract decisions from userActions (messages, choices made)
  const decisionsList = [];
  
  if (userActions && userActions.length > 0) {
    userActions.forEach((action, index) => {
      if (action.type === 'decision' || action.text) {
        decisionsList.push({
          decision: action.text || action.decision,
          context: action.context || `Task ${index + 1}`,
          impact: action.impact || 'Medium'
        });
      }
    });
  }
  
  // Fallback to decisions array if provided
  if (decisions && decisions.length > 0) {
    decisions.forEach((decision, index) => {
      decisionsList.push({
        decision: decision.description || decision,
        context: decision.context || `Decision ${index + 1}`,
        impact: decision.impact || 'Medium'
      });
    });
  }
  
  // If no decisions found, create generic ones based on scenario tasks
  if (decisionsList.length === 0) {
    return [
      { decision: 'Defined problem statement and success metrics', context: 'Discovery', impact: 'High' },
      { decision: 'Prioritized user segments and use cases', context: 'Segmentation', impact: 'High' },
      { decision: 'Aligned stakeholders on approach and timeline', context: 'Stakeholder Management', impact: 'High' }
    ];
  }
  
  return decisionsList.slice(0, 5); // Top 5 decisions
}

function extractTradeoffs(decisions, score) {
  const tradeoffs = [];
  
  // Analyze score to infer tradeoffs made
  if (score.speed > 80 && score.impact < 70) {
    tradeoffs.push({
      decision: 'Prioritized speed over comprehensive impact analysis',
      rationale: 'Fast execution was critical for stakeholder alignment'
    });
  }
  
  if (score.feasibility > 80 && score.impact < 70) {
    tradeoffs.push({
      decision: 'Chose technically feasible solution over ideal solution',
      rationale: 'Balanced innovation with implementation constraints'
    });
  }
  
  if (score.stakeholderManagement > 80 && score.speed < 70) {
    tradeoffs.push({
      decision: 'Invested time in stakeholder alignment',
      rationale: 'Ensured buy-in and reduced risk of rework'
    });
  }
  
  // Default tradeoffs if none detected
  if (tradeoffs.length === 0) {
    tradeoffs.push({
      decision: 'Balanced multiple competing priorities',
      rationale: 'Optimized for overall product success across impact, feasibility, and stakeholder needs'
    });
  }
  
  return tradeoffs;
}

function generateStakeholderAlignment(score, userActions) {
  const alignmentScore = score.stakeholderManagement || 0;
  
  let summary = '';
  if (alignmentScore >= 80) {
    summary = 'Successfully aligned all stakeholders through clear communication and structured updates';
  } else if (alignmentScore >= 60) {
    summary = 'Achieved moderate stakeholder alignment with room for improvement in communication';
  } else {
    summary = 'Stakeholder alignment needs improvement - focus on regular updates and clear communication';
  }
  
  return {
    score: alignmentScore,
    summary,
    strengths: alignmentScore >= 80 ? [
      'Clear communication',
      'Regular stakeholder updates',
      'Effective conflict resolution'
    ] : [],
    improvements: alignmentScore < 80 ? [
      'Increase frequency of updates',
      'Improve clarity of communication',
      'Better manage expectations'
    ] : []
  };
}

function generateFinalMetrics(score) {
  return {
    overallScore: score.total || 0,
    impact: score.impact || 0,
    feasibility: score.feasibility || 0,
    stakeholderManagement: score.stakeholderManagement || 0,
    speed: score.speed || 0,
    frameworkApplication: score.frameworkApplication || 0,
    productSense: {
      clearCommunication: score.clearCommunication || 0,
      productMotivation: score.productMotivation || 0,
      segmentation: score.segmentation || 0,
      problemIdentification: score.problemIdentification || 0,
      solutionDevelopment: score.solutionDevelopment || 0
    }
  };
}

function generateActionStatement(keyDecisions, userActions) {
  if (keyDecisions.length > 0) {
    const actions = keyDecisions.slice(0, 3).map(d => d.decision).join(', ');
    return `I ${actions}`;
  }
  return 'I executed the product management tasks by making data-driven decisions, collaborating with stakeholders, and applying PM frameworks';
}

function generateResultStatement(finalMetrics, score, stakeholderAlignment) {
  const results = [];
  
  if (score.total >= 80) {
    results.push(`Achieved an overall score of ${score.total.toFixed(0)}/100`);
  }
  
  if (stakeholderAlignment.score >= 80) {
    results.push('Successfully aligned all stakeholders');
  }
  
  if (score.impact >= 80) {
    results.push('Delivered high-impact solution');
  }
  
  if (score.frameworkApplication >= 80) {
    results.push('Applied PM frameworks effectively');
  }
  
  if (results.length === 0) {
    results.push('Completed the scenario with measurable outcomes');
  }
  
  return results.join('. ') + '.';
}

function formatFullText(starFormat, keyDecisions, tradeoffs, finalMetrics) {
  return `**Situation:** ${starFormat.situation}

**Task:** ${starFormat.task}

**Action:** ${starFormat.action}

**Result:** ${starFormat.result}

**Key Decisions:**
${keyDecisions.map((d, i) => `${i + 1}. ${d.decision} (${d.context})`).join('\n')}

**Tradeoffs:**
${tradeoffs.map((t, i) => `${i + 1}. ${t.decision} - ${t.rationale}`).join('\n')}

**Final Metrics:**
- Overall Score: ${finalMetrics.overallScore.toFixed(0)}/100
- Impact: ${finalMetrics.impact.toFixed(0)}/100
- Stakeholder Management: ${finalMetrics.stakeholderManagement.toFixed(0)}/100
- Framework Application: ${finalMetrics.frameworkApplication.toFixed(0)}/100`;
}

